import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { 
  ArrowBack, 
  Refresh, 
  Download, 
  Share, 
  Email
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/authService';

const MeetingAnalyticsSummary = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [shareLink, setShareLink] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);

  const fetchMeetingSummary = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/analytics/meetings/${id}/summary`);
      setMeeting(response.data);
    } catch (err) {
      console.error('Error fetching meeting summary:', err);
      setError('Failed to load meeting analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetingSummary();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRecalculate = async () => {
    try {
      setCalculating(true);
      await api.post(`/analytics/meetings/${id}/calculate`, {});
      await fetchMeetingSummary();
    } catch (err) {
      console.error('Error calculating analytics:', err);
      setError('Failed to calculate analytics');
    } finally {
      setCalculating(false);
    }
  };

  const handleDownloadParticipantCSV = async () => {
    try {
      const response = await api.get(
        `/analytics/meetings/${id}/export/participants-csv`,
        {
          responseType: 'blob'
        }
      );
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `participants-${meeting.meetingCode}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading CSV:', err);
      setError('Failed to download participant CSV');
    }
  };

  const handleGenerateShareLink = async () => {
    try {
      const response = await api.post(
        `/analytics/meetings/${id}/share`,
        { expiresInDays: 30 }
      );
      const baseUrl = window.location.origin;
      setShareLink(`${baseUrl}/analytics/shared/${response.data.token}`);
      setShowShareDialog(true);
    } catch (err) {
      console.error('Error generating share link:', err);
      setError('Failed to generate shareable link');
    }
  };

  const handleSendEmailSummary = async () => {
    try {
      await api.post(
        `/analytics/meetings/${id}/send-summary`,
        {}
      );
      alert('Email summary sent successfully!');
    } catch (err) {
      console.error('Error sending email summary:', err);
      setError('Failed to send email summary');
    }
  };

  const handleCopyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    alert('Link copied to clipboard!');
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return hours > 0 
      ? `${hours}h ${minutes}m ${secs}s` 
      : minutes > 0 
      ? `${minutes}m ${secs}s` 
      : `${secs}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!meeting) {
    return (
      <Box>
        <Alert severity="error">Meeting not found</Alert>
        <Button onClick={() => navigate('/analytics')} sx={{ mt: 2 }}>
          Back to Analytics
        </Button>
      </Box>
    );
  }

  const analytics = meeting.analytics;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/analytics')}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Meeting Analytics: {meeting.title}
        </Typography>
        {analytics && (
          <>
            <Button
              startIcon={<Email />}
              onClick={handleSendEmailSummary}
              variant="outlined"
              color="primary"
            >
              Email Summary
            </Button>
            <Button
              startIcon={<Share />}
              onClick={handleGenerateShareLink}
              variant="outlined"
              color="primary"
            >
              Share
            </Button>
            <Button
              startIcon={<Download />}
              onClick={handleDownloadParticipantCSV}
              variant="outlined"
              color="primary"
            >
              Export CSV
            </Button>
            <Button
              startIcon={<Refresh />}
              onClick={handleRecalculate}
              disabled={calculating}
              variant="outlined"
            >
              {calculating ? 'Calculating...' : 'Recalculate'}
            </Button>
          </>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!analytics && meeting.status === 'ended' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Analytics not yet calculated for this meeting.
          <Button onClick={handleRecalculate} sx={{ ml: 2 }}>
            Calculate Now
          </Button>
        </Alert>
      )}

      {meeting.status !== 'ended' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This meeting has not ended yet. Analytics will be available after the meeting ends.
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Meeting Details
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Meeting Code
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {meeting.meetingCode}
                </Typography>

                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  Started At
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDate(meeting.actualStartTime)}
                </Typography>

                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  Ended At
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDate(meeting.actualEndTime)}
                </Typography>

                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  Duration
                </Typography>
                <Typography variant="body1">
                  {formatDuration(analytics?.totalDurationSeconds)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Summary Metrics
              </Typography>
              {analytics ? (
                <Box sx={{ mt: 2 }}>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography>Participants</Typography>
                    <Typography fontWeight="bold">
                      {analytics.participantCount}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography>Chat Messages</Typography>
                    <Typography fontWeight="bold">
                      {analytics.totalChatMessages}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography>Hand Raises</Typography>
                    <Typography fontWeight="bold">
                      {analytics.handRaiseCount}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography>Screen Share Time</Typography>
                    <Typography fontWeight="bold">
                      {formatDuration(analytics.screenShareSeconds)}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography color="textSecondary">
                  Analytics not available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {analytics && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Engagement Score
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h2">
                      {Math.round(analytics.engagementScore)}
                    </Typography>
                    <Chip
                      label={
                        analytics.engagementScore >= 80
                          ? 'Excellent'
                          : analytics.engagementScore >= 60
                          ? 'Good'
                          : 'Needs Improvement'
                      }
                      color={getScoreColor(analytics.engagementScore)}
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    Based on chat participation, screen sharing, and hand raises
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quality Score
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h2">
                      {Math.round(analytics.qualityScore)}
                    </Typography>
                    <Chip
                      label={
                        analytics.qualityScore >= 80
                          ? 'Excellent'
                          : analytics.qualityScore >= 60
                          ? 'Good'
                          : 'Poor'
                      }
                      color={getScoreColor(analytics.qualityScore)}
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    Based on jitter, RTT, packet loss, and connection stability
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Network Quality Metrics
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <Typography color="textSecondary" variant="body2">
                    Avg Jitter
                  </Typography>
                  <Typography variant="h6">
                    {analytics.avgJitterMs?.toFixed(1) || 0} ms
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography color="textSecondary" variant="body2">
                    Avg RTT
                  </Typography>
                  <Typography variant="h6">
                    {analytics.avgRttMs?.toFixed(1) || 0} ms
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography color="textSecondary" variant="body2">
                    Avg Packet Loss
                  </Typography>
                  <Typography variant="h6">
                    {analytics.avgPacketLossPct?.toFixed(2) || 0}%
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {analytics.participantAnalytics && analytics.participantAnalytics.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Participant Metrics
                </Typography>
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Join Time</TableCell>
                        <TableCell>Speaking %</TableCell>
                        <TableCell>Chat Messages</TableCell>
                        <TableCell>Avg RTT</TableCell>
                        <TableCell>Reconnects</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.participantAnalytics.map((participant) => (
                        <TableRow key={participant.id}>
                          <TableCell>{participant.displayName}</TableCell>
                          <TableCell>{formatDate(participant.joinTime)}</TableCell>
                          <TableCell>
                            {participant.speakingPercentage?.toFixed(1) || 0}%
                          </TableCell>
                          <TableCell>{participant.chatMessagesCount}</TableCell>
                          <TableCell>
                            {participant.avgRttMs?.toFixed(1) || 0} ms
                          </TableCell>
                          <TableCell>{participant.reconnectsCount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Share Link Dialog */}
      <Dialog 
        open={showShareDialog} 
        onClose={() => setShowShareDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Shareable Analytics Link</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share this link to allow others to view the analytics for this meeting. 
            The link will expire in 30 days.
          </Typography>
          <TextField
            fullWidth
            value={shareLink || ''}
            InputProps={{
              readOnly: true,
            }}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowShareDialog(false)}>Close</Button>
          <Button onClick={handleCopyShareLink} variant="contained">
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MeetingAnalyticsSummary;
